import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, Download, CheckCircle2, ShieldCheck, FileSignature, Receipt, CreditCard } from "lucide-react";
import { Logo } from "@/components/Logo";
import { IOSButton, StatusBadge, InsetList, CARD_SHADOW } from "@/components/ios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (cents, currency = "usd") =>
  `${currency === "usd" ? "$" : ""}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/portal/${token}`)
      .then((res) => setData(res.data))
      .catch((e) => {
        const status = e.response?.status;
        setError(
          status === 410
            ? "This portal link has expired. Ask the sender for a fresh link."
            : status === 429
              ? "Too many attempts. Wait a moment and reload."
              : "This portal link is not valid. Check the link or ask the sender for a new one."
        );
      });
  }, [token]);

  const downloadContract = async (c) => {
    setDownloading(c.id);
    try {
      const res = await axios.get(`${API}/portal/${token}/contracts/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(c.title || "document").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-signed.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFB] text-[#0A0A0A] font-sans" data-testid="portal-page">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-2xl backdrop-saturate-150 border-b border-black/[0.05]">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo size={26} />
          <span className="flex items-center gap-1.5 text-[13px] font-medium text-[#3C3C43]/60">
            <ShieldCheck className="w-4 h-4 text-[#34C759]" strokeWidth={1.5} /> Secure client portal
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 md:py-12">
        {error && (
          <div className={`bg-white rounded-3xl p-10 text-center ${CARD_SHADOW}`} data-testid="portal-error">
            <p className="text-[17px] font-semibold mb-2">Portal unavailable</p>
            <p className="text-[15px] text-[#3C3C43]/60">{error}</p>
          </div>
        )}
        {!data && !error && (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 text-[#3C3C43]/40 animate-spin" strokeWidth={1.5} />
          </div>
        )}

        {data && (
          <>
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="portal-title">
                Documents{data.client_name ? ` for ${data.client_name}` : ""}
              </h1>
              <p className="text-[15px] text-[#3C3C43]/60 mt-1">Your contracts and invoices from Doclyx, in one place.</p>
            </div>

            <section className="mb-10">
              <div className="flex items-center gap-2 mb-3 px-1">
                <FileSignature className="w-4 h-4 text-[#002B5B]" strokeWidth={1.5} />
                <h2 className="text-[13px] font-semibold uppercase tracking-wide text-[#3C3C43]/60">Contracts</h2>
              </div>
              {data.contracts.length === 0 ? (
                <div className={`bg-white rounded-sm p-8 text-center ${CARD_SHADOW}`} data-testid="portal-contracts-empty">
                  <p className="text-[15px] text-[#3C3C43]/55">No shared contracts yet.</p>
                </div>
              ) : (
                <InsetList testid="portal-contracts">
                  {data.contracts.map((c, i) => (
                    <div key={c.id} className="flex items-center gap-3 px-5 py-4 min-h-[64px]" data-testid={`portal-contract-row-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-semibold text-[#0A0A0A] truncate">{c.title}</p>
                        <p className="text-[13px] text-[#3C3C43]/55 truncate">
                          {c.status === "signed"
                            ? `Signed by ${c.signer_name || "you"} · ${c.signed_at ? new Date(c.signed_at).toLocaleDateString() : ""}`
                            : "Awaiting signature"}
                        </p>
                      </div>
                      <StatusBadge status={c.status} />
                      {c.status === "signed" && (
                        <button
                          type="button"
                          data-testid={`portal-contract-download-${i}`}
                          onClick={() => downloadContract(c)}
                          disabled={downloading === c.id}
                          aria-label={`Download ${c.title}`}
                          className="w-9 h-9 rounded-full bg-[#002B5B]/10 text-[#002B5B] flex items-center justify-center active:scale-95 transition-transform duration-150 disabled:opacity-40"
                        >
                          {downloading === c.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                          ) : (
                            <Download className="w-4 h-4" strokeWidth={1.5} />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </InsetList>
              )}
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3 px-1">
                <Receipt className="w-4 h-4 text-[#002B5B]" strokeWidth={1.5} />
                <h2 className="text-[13px] font-semibold uppercase tracking-wide text-[#3C3C43]/60">Invoices</h2>
              </div>
              {data.invoices.length === 0 ? (
                <div className={`bg-white rounded-sm p-8 text-center ${CARD_SHADOW}`} data-testid="portal-invoices-empty">
                  <p className="text-[15px] text-[#3C3C43]/55">No invoices yet.</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="portal-invoices">
                  {data.invoices.map((inv, i) => (
                    <div key={inv.id} className={`bg-white rounded-sm p-5 ${CARD_SHADOW}`} data-testid={`portal-invoice-row-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[15px] font-semibold text-[#0A0A0A] truncate">{inv.number || "Invoice"}</p>
                          <p className="text-[13px] text-[#3C3C43]/55 mt-0.5 line-clamp-2">{inv.description}</p>
                          <p className="text-[12px] text-[#3C3C43]/40 mt-1.5">
                            {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ""}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[17px] font-bold tabular-nums">{money(inv.amount_cents, inv.currency)}</p>
                          <div className="mt-1 flex justify-end">
                            <StatusBadge status={inv.status} />
                          </div>
                        </div>
                      </div>
                      {inv.status === "open" && inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="block mt-4" data-testid={`portal-invoice-pay-${i}`}>
                          <IOSButton className="w-full">
                            <CreditCard className="w-4 h-4" strokeWidth={1.5} /> Pay now — {money(inv.amount_cents, inv.currency)}
                          </IOSButton>
                        </a>
                      )}
                      {inv.status === "paid" && (
                        <p className="flex items-center gap-1.5 text-[13px] font-medium text-[#248A3D] mt-4" data-testid={`portal-invoice-paid-${i}`}>
                          <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                          Paid{inv.invoice_pdf && (
                            <>
                              {" · "}
                              <a
                                href={inv.invoice_pdf}
                                target="_blank"
                                rel="noreferrer"
                                className="underline underline-offset-2"
                                data-testid={`portal-invoice-receipt-${i}`}
                              >
                                Receipt PDF
                              </a>
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-5 pb-10 text-center">
        <Link to="/" className="text-[12px] text-[#3C3C43]/40 hover:text-[#002B5B] transition-colors duration-150">
          Powered by Doclyx
        </Link>
      </footer>
    </div>
  );
}
